"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Landing "From the Guide Center" strip: two guide cards + a browse-all
 * CTA, placed before the FAQ so the honesty-first guides do trust work
 * right where objections form. Card copy reuses each guide's own hero
 * keys via GUIDE_INDEX (see GuideHubPage.tsx) — one source of truth.
 */

import { Link } from "react-router-dom"
import { useI18n } from "@/i18n"
import { GUIDE_INDEX } from "@/routes/guides/GuideHubPage"
import { Reveal } from "./Reveal"

export function GuidesStrip() {
  const { t } = useI18n()

  return (
    <section id="guides" aria-labelledby="guides-heading" className="bg-paper">
      <div className="mx-auto max-w-6xl px-4 py-16 md:px-8 md:py-24">
        <Reveal>
          <p className="text-sm font-semibold tracking-wide text-green uppercase">
            {t("landingPage.guidesStrip.eyebrow")}
          </p>
          <h2
            id="guides-heading"
            className="mt-3 max-w-2xl text-3xl leading-tight font-black text-ink md:text-4xl"
          >
            {t("landingPage.guidesStrip.heading")}
          </h2>
          <p className="mt-4 max-w-prose text-base text-ink-2 md:text-lg">
            {t("landingPage.guidesStrip.subtitle")}
          </p>
        </Reveal>

        {/* Craft fix C7 (2026-09-10): three guides in a two-column grid left
            one card alone beside a large hole at desktop. */}
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {GUIDE_INDEX.map(guide => (
            <Reveal key={guide.path}>
              <Link
                to={guide.path}
                className="group flex h-full flex-col gap-3 rounded-bb-xl border border-line bg-cream p-6 transition-all hover:-translate-y-0.5 hover:border-green/60 hover:shadow-[var(--bb-shadow)] md:p-8"
              >
                <p className="text-xs font-semibold tracking-wide text-green uppercase">
                  {t(`${guide.heroKey}.readTime`)}
                </p>
                <h3 className="text-xl leading-snug font-bold text-ink md:text-2xl">
                  {t(`${guide.heroKey}.title`)}
                </h3>
                <p className="text-sm leading-relaxed text-ink-2 md:text-base">
                  {t(`${guide.heroKey}.subtitle`)}
                </p>
                <span className="mt-auto pt-2 text-sm font-semibold text-green group-hover:underline">
                  {t("guides.hub.readCta")} →
                </span>
              </Link>
            </Reveal>
          ))}
        </div>

        <div className="mt-8">
          <Link
            to="/guides"
            className="inline-flex min-h-11 items-center rounded-bb-full border border-line bg-cream px-5 text-sm font-semibold text-ink transition-colors hover:border-green/60 hover:bg-paper"
          >
            {t("landingPage.guidesStrip.allCta")} →
          </Link>
        </div>
      </div>
    </section>
  )
}
